import { format } from 'date-fns';
import React, { memo } from 'react';
import { useAuthContext } from '@emfood/shared';
import { useNavigate } from '@tanstack/react-router';
import { MRT_PaginationState } from 'material-react-table';
import Iconify from '@emfood/shared/src/components/iconify';

import {
  Box,
  Card,
  Link,
  Stack,
  Divider,
  Tooltip,
  Typography,
  IconButton,
  Pagination,
  CircularProgress,
} from '@mui/material';

import { paths } from 'src/routes/paths';

import { LocalProductDetailsObject } from 'src/api/models/products';
import { DetailsFavouritesCollectionModel } from 'src/api/models/favourites';

import PriceLabel from 'src/components/products/price-label';
import { QuantityInput } from 'src/components/products/quantity-input';
import SurplusProductLabel from 'src/components/products/surplus-product-label';
import AddToCartButtonMobile from 'src/components/products/add-to-cart-button-mobile';
import ProductQualityBadges from 'src/components/products/product-quality-badges';
import PromotionalProductLabel from 'src/components/products/promotional-product-label';
import UpdateProductButtonMobile from 'src/components/products/update-product-button-mobile';

import AddProductToFavouritesCollectionDialog from './add-product-to-favourites.collection-dialog';
import AddProductToFavouritesCateringManagerDialog from './add-product-to-favourites-catering-manager-dialog';

interface MobileProductListProps {
  products: LocalProductDetailsObject[];
  isLoading?: boolean;
  personalFavouritesCollections?: DetailsFavouritesCollectionModel[];
  favouritesCollectionsData?: any;
  isFavouritesLoading?: boolean;
  favouritesCollectionsError?: unknown;
  paginationModel?: MRT_PaginationState;
  totalPages?: number;
  onPaginationModelChanged?: (newPaginationModel: MRT_PaginationState) => void;
  handleAddToCart: (productStockId: string, quantity: number, rowId: string) => void;
  handleUpdate: (cartItemId: string, quantity: number, rowId: string) => void;
  handleRemoveFromCart: (cartItemId: string, rowId: string) => void;
  handleQuantityChange: (productStockId: string, change: number) => void;
}

const MobileProductList = memo(
  ({
    products,
    isLoading,
    personalFavouritesCollections,
    favouritesCollectionsData,
    isFavouritesLoading,
    favouritesCollectionsError,
    paginationModel,
    onPaginationModelChanged,
    totalPages,
    handleAddToCart,
    handleUpdate,
    handleRemoveFromCart,
    handleQuantityChange,
  }: MobileProductListProps) => {
    const { user } = useAuthContext();
    const [selectedProduct, setSelectedProduct] = React.useState<string | null>(null);
    const [isDialogOpen, setIsDialogOpen] = React.useState(false);

    const isCateringManager =
      user?.roles?.some((role: any) => role.name === 'Catering Manager') || false;

    const navigate = useNavigate();

    if (isLoading) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <CircularProgress />
        </Box>
      );
    }

    if (!products?.length) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
          <Typography>No products found</Typography>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: {
            xs: '1fr', // Mobile: 1 card per row
            sm: 'repeat(2, 1fr)', // Tablet: 2 cards per row
            md: 'repeat(2, 1fr)', // Desktop: 2 cards per row
          },
          p: { xs: 0, sm: 2 },
        }}
      >
        {products.map((product) => (
          <Card key={product.productStockId} sx={{ p: 2 }}>
            <Stack spacing={2}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                {product.surplusEntries?.typeOfPrice === 'surplus' && <SurplusProductLabel />}
                {product.surplusEntries?.typeOfPrice === 'promotion' && <PromotionalProductLabel />}
              </Stack>

              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Stack spacing={1} flex={1}>
                  <Link
                    component="button"
                    onClick={() =>
                      navigate({
                        to: paths.dashboard.products.productDetails(product.productStockId),
                      })
                    }
                    sx={{ textAlign: 'left' }}
                  >
                    <Typography variant="h3" color="text.primary">
                      {product.name}
                    </Typography>
                    <Typography variant="h4" color="GrayText">
                      {product.units}
                    </Typography>
                  </Link>
                  <ProductQualityBadges
                    certifications={product.certifications}
                    dietaryLifestyles={product.dietaryLifestyles}
                    isLocalProducer={product.isLocalProducer}
                  />
                  {product.producerId ? (
                    <Link
                      component="button"
                      onClick={() => {
                        navigate({
                          to: paths.dashboard.producer.producerDetails(String(product.producerId)),
                        });
                      }}
                    >
                      <Typography variant="body2" color="textPrimary" align="left">
                        {product.producer}
                      </Typography>
                    </Link>
                  ) : (
                    <Typography variant="body2">{product.brand}</Typography>
                  )}
                </Stack>
                <Tooltip title="Add to Favourite">
                  <IconButton
                    onClick={() => {
                      setSelectedProduct(product.productStockId);
                      setIsDialogOpen(true);
                    }}
                    aria-label="Add to Favourites"
                  >
                    <Iconify
                      icon={
                        product.hasLinkedFavouritesLists ? 'eva:heart-fill' : 'eva:heart-outline'
                      }
                    />
                  </IconButton>
                </Tooltip>
              </Stack>

              <Stack spacing={1}>
                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Supplier:
                  </Typography>
                  <Stack alignItems="flex-end" sx={{ ml: 2 }}>
                    <Typography variant="body2" align="right">
                      {product.distributorOrg?.name ?? '---'}
                    </Typography>
                    {product.distributorReference && (
                      <Typography variant="caption" color="text.secondary" align="right">
                        Ref: {product.distributorReference}
                      </Typography>
                    )}
                  </Stack>
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Available From:
                  </Typography>
                  <Typography variant="body2">
                    {product.earliestDeliveryDate
                      ? format(new Date(product.earliestDeliveryDate), 'dd/MM/yyyy')
                      : 'N/A'}
                  </Typography>
                </Stack>

                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Price:
                  </Typography>
                  <PriceLabel
                    direction="row"
                    price={product.activePrice}
                    surplusEntry={product.surplusEntries}
                    isEstimate={product.weightType === 'Guide Weight'}
                  />
                </Stack>

                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="body2" color="text.secondary">
                    Quantity:
                  </Typography>
                  <QuantityInput
                    productName={product.name}
                    value={product.quantity}
                    onChange={(value) =>
                      handleQuantityChange(product.productStockId, value - product.quantity)
                    }
                    minimumValue={1}
                    maximumValue={Number(product.surplusEntries?.reservedQuantity)}
                  />
                </Stack>

                {product.surplusEntries?.reservedQuantity && (
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary">
                      Available Stock:
                    </Typography>
                    <Typography variant="body2">
                      {product.surplusEntries.reservedQuantity}
                    </Typography>
                  </Stack>
                )}

                <Stack direction="row" justifyContent="space-between">
                  <Typography variant="body2" color="text.secondary">
                    Total Price:
                  </Typography>
                  <Typography variant="body2">{product.totalPrice}</Typography>
                </Stack>
              </Stack>

              <Divider />

              <Stack direction="row" justifyContent="space-between" alignItems="center">
                {product.isInCart ? (
                  <Stack direction="row" spacing={1} width="100%" alignItems="center">
                    <UpdateProductButtonMobile
                      isDisabled={product.quantity.toString() === product.cartQuantity.toString()}
                      onClick={() =>
                        handleUpdate(
                          product.cartItemId,
                          product.quantity,
                          product.productStockId
                        )
                      }
                      quantity={product.quantity}
                      maxQuantity={Number(product.surplusEntries?.reservedQuantity)}
                      insufficientStockText="Insufficient Stock"
                    />
                    <Tooltip title="Remove from basket">
                      <IconButton
                        aria-label="Remove from basket"
                        onClick={() =>
                          handleRemoveFromCart(product.cartItemId, product.productStockId)
                        }
                      >
                        <Iconify icon="solar:trash-bin-trash-bold" />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                ) : (
                  <AddToCartButtonMobile
                    onClick={() =>
                      handleAddToCart(
                        product.productStockId,
                        product.quantity,
                        product.productStockId
                      )
                    }
                    quantity={product.quantity}
                    maxQuantity={Number(product.surplusEntries?.reservedQuantity)}
                  />
                )}
              </Stack>
            </Stack>
          </Card>
        ))}

        {paginationModel && (
          <Pagination
            count={totalPages}
            page={paginationModel.pageIndex + 1}
            onChange={(_event, page) => {
              if (onPaginationModelChanged) {
                const newPaginationModel = { ...paginationModel, pageIndex: page - 1 };
                onPaginationModelChanged(newPaginationModel);
              }
            }}
          />
        )}

        {isCateringManager && favouritesCollectionsData ? (
          <AddProductToFavouritesCateringManagerDialog
            open={isDialogOpen}
            onClose={() => {
              setIsDialogOpen(false);
              setSelectedProduct(null);
            }}
            favouritesCollectionsData={favouritesCollectionsData}
            isFavouritesLoading={isFavouritesLoading}
            favouritesCollectionsError={favouritesCollectionsError}
            productStockId={selectedProduct || ''}
            preSelectedFavourites={[]}
          />
        ) : (
          <AddProductToFavouritesCollectionDialog
            open={isDialogOpen}
            onClose={() => {
              setIsDialogOpen(false);
              setSelectedProduct(null);
            }}
            personalFavouritesCollections={personalFavouritesCollections}
            sharedFavouritesCollections={[
              ...(favouritesCollectionsData?.orgShared ?? []),
              ...(favouritesCollectionsData?.siteShared ?? []),
            ]}
            isFavouritesLoading={isFavouritesLoading}
            favouritesCollectionsError={favouritesCollectionsError}
            productStockId={selectedProduct || ''}
            preSelectedFavourites={[]}
          />
        )}
      </Box>
    );
  }
);

export default MobileProductList;
